import React, { useState } from 'react';
import { CheckCircle, Circle, AlertCircle, FileText } from 'lucide-react';
import Button from '../../../components/ui/Button';

const WhatIfChecklist = ({ searchQuery, filterLocation }) => {
  const [expandedSections, setExpandedSections] = useState(['startup']);

  const checklistSections = [
    {
      id: 'startup',
      title: 'Startup Operations',
      questions: [
        {
          id: 'Q1',
          question: 'What if the startup sequence is not followed correctly?',
          hazards: ['Equipment damage', 'Process upset', 'Safety system bypass'],
          safeguards: ['Procedure checklist', 'Operator training', 'Supervisor verification'],
          status: 'reviewed',
          riskLevel: 'high',
        },
        {
          id: 'Q2',
          question: 'What if pressure is applied too quickly during startup?',
          hazards: ['Thermal shock', 'Gasket failure', 'Pipe rupture'],
          safeguards: ['Pressure ramp rate control', 'Monitoring', 'Pressure relief'],
          status: 'reviewed',
          riskLevel: 'high',
        },
        {
          id: 'Q3',
          question: 'What if equipment is not properly isolated before startup?',
          hazards: ['Cross-contamination', 'Unintended equipment operation', 'Safety hazard'],
          safeguards: ['Isolation verification', 'LOTO procedures', 'Pre-startup checklist'],
          status: 'pending',
          riskLevel: 'critical',
        },
      ],
    },
    {
      id: 'normal',
      title: 'Normal Operations',
      questions: [
        {
          id: 'Q4',
          question: 'What if flow rate exceeds design limits?',
          hazards: ['Equipment overload', 'Erosion', 'Vibration damage'],
          safeguards: ['Flow monitoring', 'High flow alarm', 'Flow control valve'],
          status: 'reviewed',
          riskLevel: 'medium',
        },
        {
          id: 'Q5',
          question: 'What if temperature control fails during operation?',
          hazards: ['Runaway reaction', 'Product degradation', 'Equipment damage'],
          safeguards: ['Temperature monitoring', 'High temp alarm', 'Emergency cooling'],
          status: 'reviewed',
          riskLevel: 'high',
        },
      ],
    },
    {
      id: 'shutdown',
      title: 'Shutdown Operations',
      questions: [
        {
          id: 'Q6',
          question: 'What if emergency shutdown is activated during normal operation?',
          hazards: ['Process upset', 'Product loss', 'Equipment stress'],
          safeguards: ['ESD procedures', 'Operator training', 'Controlled shutdown sequence'],
          status: 'pending',
          riskLevel: 'high',
        },
      ],
    },
    {
      id: 'maintenance',
      title: 'Maintenance Activities',
      questions: [
        {
          id: 'Q7',
          question: 'What if maintenance is performed on wrong equipment?',
          hazards: ['Operational disruption', 'Safety incident', 'Equipment damage'],
          safeguards: ['Equipment tagging', 'Work permit system', 'Supervisor verification'],
          status: 'reviewed',
          riskLevel: 'critical',
        },
        {
          id: 'Q8',
          question: 'What if equipment is not properly depressurized before maintenance?',
          hazards: ['Pressure release', 'Personnel injury', 'Chemical exposure'],
          safeguards: ['Depressurization procedure', 'Pressure verification', 'LOTO'],
          status: 'pending',
          riskLevel: 'critical',
        },
      ],
    },
  ];

  const toggleSection = (sectionId) => {
    setExpandedSections(prev =>
      prev?.includes(sectionId)
        ? prev?.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const getStatusBadge = (status) => {
    return status === 'reviewed'
      ? { icon: CheckCircle, color: 'text-green-600 bg-green-50', label: 'Reviewed' }
      : { icon: Circle, color: 'text-yellow-600 bg-yellow-50', label: 'Pending Review' };
  };

  const getRiskBadgeColor = (level) => {
    const colors = {
      critical: 'bg-red-100 text-red-800 border-red-300',
      high: 'bg-orange-100 text-orange-800 border-orange-300',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      low: 'bg-green-100 text-green-800 border-green-300',
    };
    return colors?.[level] || colors?.medium;
  };

  const totalQuestions = checklistSections?.reduce((sum, section) => sum + section?.questions?.length, 0);
  const reviewedQuestions = checklistSections?.reduce((sum, section) => 
    sum + section?.questions?.filter(q => q?.status === 'reviewed')?.length, 0
  );

  return (
    <div className="space-y-6">
      {/* Progress Summary */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Checklist Progress</h3>
            <p className="text-sm text-gray-600 mt-1">
              {reviewedQuestions} of {totalQuestions} questions reviewed
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-blue-600">
              {Math.round((reviewedQuestions / totalQuestions) * 100)}%
            </div>
            <div className="text-xs text-gray-500">Complete</div>
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="bg-blue-600 h-3 rounded-full transition-all duration-300"
            style={{ width: `${(reviewedQuestions / totalQuestions) * 100}%` }}
          ></div>
        </div>
      </div>
      {/* Checklist Sections */}
      <div className="space-y-4">
        {checklistSections?.map((section) => {
          const isExpanded = expandedSections?.includes(section?.id);
          const sectionReviewed = section?.questions?.filter(q => q?.status === 'reviewed')?.length;
          const sectionTotal = section?.questions?.length;

          return (
            <div key={section?.id} className="bg-white rounded-lg shadow overflow-hidden">
              {/* Section Header */}
              <button
                onClick={() => toggleSection(section?.id)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <div className="text-left">
                    <h3 className="font-semibold text-gray-900">{section?.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {sectionReviewed}/{sectionTotal} reviewed
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-sm font-medium text-gray-600">
                    {Math.round((sectionReviewed / sectionTotal) * 100)}%
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              {/* Section Content */}
              {isExpanded && (
                <div className="border-t border-gray-200">
                  {section?.questions?.map((question, idx) => {
                    const StatusIcon = getStatusBadge(question?.status)?.icon;
                    return (
                      <div
                        key={question?.id}
                        className={`px-6 py-4 ${idx !== 0 ? 'border-t border-gray-100' : ''}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0 mt-1">
                            <StatusIcon className={`w-5 h-5 ${getStatusBadge(question?.status)?.color?.split(' ')?.[0]}`} />
                          </div>

                          <div className="flex-1 space-y-3">
                            {/* Question Header */}
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">{question?.question}</div>
                                <div className="text-xs text-gray-500 mt-1">{question?.id}</div>
                              </div>
                              <div className="flex gap-2">
                                <span className={`px-2.5 py-1 rounded text-xs font-medium border ${getRiskBadgeColor(question?.riskLevel)}`}>
                                  {question?.riskLevel?.toUpperCase()}
                                </span>
                                <span className={`px-2.5 py-1 rounded text-xs font-medium ${getStatusBadge(question?.status)?.color}`}>
                                  {getStatusBadge(question?.status)?.label}
                                </span>
                              </div>
                            </div>

                            {/* Hazards */}
                            <div>
                              <div className="text-xs font-medium text-gray-500 mb-2">Potential Hazards:</div>
                              <div className="flex flex-wrap gap-2">
                                {question?.hazards?.map((hazard, hIdx) => (
                                  <span key={hIdx} className="px-2 py-1 bg-red-50 text-red-700 text-xs rounded border border-red-200">
                                    <AlertCircle className="w-3 h-3 inline mr-1" />
                                    {hazard}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* Safeguards */}
                            <div>
                              <div className="text-xs font-medium text-gray-500 mb-2">Existing Safeguards:</div>
                              <div className="flex flex-wrap gap-2">
                                {question?.safeguards?.map((safeguard, sIdx) => (
                                  <span key={sIdx} className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded border border-green-200">
                                    <CheckCircle className="w-3 h-3 inline mr-1" />
                                    {safeguard}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2 pt-2">
                              <Button size="sm" variant="outline">
                                Edit Assessment
                              </Button>
                              {question?.status === 'pending' && (
                                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white">
                                  Mark as Reviewed
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WhatIfChecklist;